import { useState } from 'react'
import {
  Badge,
  Box, Button, ButtonGroup, Checkbox, ClickAwayListener, Divider, FormControlLabel, Grow,
  ListItemText, MenuItem, MenuList, Paper, Popper, Stack, Switch, Typography
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useCropModalContext, useCropModalContextDispatch, setBulkAdd } from '../context/crop-modal-context'
import { CropWarningBanner } from './crop-warning-banner'
import { TpTooltip } from '~/components'
import { useFeatures } from '~/common/use-features'

export const CropModalFooter = ({
  selectedOrgId,
  selectedOrgName,
  hierarchalOrgs,
  farmName,
  plotName,
  patchName,
  handlers,
  previewScheduleData,
  frostDates,
  seasonRequired,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const dropdownOpen = Boolean(anchorEl)
  const { features } = useFeatures()

  const { species, showNotes, cropId, bulkAdd, newCropsQueue, isCustomCrop, customSpeciesName } = useCropModalContext()
  const dispatch = useCropModalContextDispatch()
  const { handleOrgSelect, handleToggleNotes, _onClose, onSubmit } = handlers

  const missingSpeciesOrLocation = (!species && !isCustomCrop) || !selectedOrgId
    || (isCustomCrop && !customSpeciesName?.trim())

  const submitLabel = (
    <>
      <strong>
        {cropId
          ? 'Save'
          : bulkAdd
            ? 'Queue'
            : 'Submit'
        }
      </strong>
      <span style={{ marginLeft: 4, whiteSpace: 'pre' }}>
        to {(selectedOrgName || patchName || plotName || farmName).slice(0, 10)}
        {(selectedOrgName || patchName || plotName || farmName).length > 10 ? '...' : ''}
      </span>
    </>
  )

  return (
    <>
      <Box
        sx={{
          mt: '1rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          variant='text'
          size='large'
          startIcon={showNotes ? <Icons.NotesTwoTone /> : <Icons.NoteAddTwoTone />}
          onClick={handleToggleNotes}
          sx={{
            minWidth: 'fit-content',
          }}
        >
          <Typography variant='body1' color='text.primary'>
            {showNotes ? 'Hide' : 'Add'} Notes
          </Typography>
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CropWarningBanner previewScheduleData={previewScheduleData} frostDates={frostDates} />

          <Box sx={{ display: 'flex', gap: '1rem', flexShrink: 0 }}>
            {/* BULK ADD CHECKBOX */}
            {features['add_crops_bulk'] && !cropId && (
              <TpTooltip enterDelay={200} title='Create this crop and keep modal open for another crop'>
                <Badge
                  showZero={false}
                  badgeContent={newCropsQueue.length}
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      border: (theme) => `2px solid ${theme.palette.background.paper}`,
                    },
                  }}
                >
                  <Button
                    variant={bulkAdd ? 'contained' : 'outlined'}
                    size='large'
                    endIcon={<Icons.Inventory2TwoTone />}
                    onClick={() => dispatch(setBulkAdd({ bulkAdd: !bulkAdd }))}
                    sx={{
                      minWidth: 'fit-content',
                    }}
                  >
                    <Typography variant='body1' color={bulkAdd ? 'primary.contrastText' : 'text.primary'}>Bulk</Typography>
                  </Button>
                </Badge>
              </TpTooltip>
            )}

            <Button variant='outlined' size='large' onClick={_onClose}>
              Cancel
            </Button>

            {/* SUBMIT AND ORGANIZATION SELECTOR */}
            <ButtonGroup variant="contained" size="large">
              <Button
                disabled={missingSpeciesOrLocation || seasonRequired}
                onClick={onSubmit}
                title={seasonRequired ? 'Select a season before adding a crop' : undefined}
                sx={{ minWidth: '170px', width: '170px' }}
              >
                {submitLabel}
              </Button>
              <Button
                disabled={missingSpeciesOrLocation || seasonRequired}
                aria-controls={dropdownOpen ? 'org-selector-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={dropdownOpen ? 'true' : undefined}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ px: 1, minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icons.ArrowDropDown />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {/* Organization Selector Dropdown */}
      <Popper
        open={dropdownOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        disablePortal={false}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={8} sx={{ mt: 0.5 }}>
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <MenuList id="org-selector-menu" sx={{ minWidth: 200 }}>
                  {hierarchalOrgs.length === 0 ? (
                    <MenuItem disabled>
                      <ListItemText primary="No organizations available" />
                    </MenuItem>
                  ) : (
                    hierarchalOrgs.map((org) => (
                      <Box key={org.id}>
                        <MenuItem
                          selected={selectedOrgId === org.id}
                          onClick={() => { handleOrgSelect(org.id, org.name); setAnchorEl(null) }}
                        >
                          <Box sx={{ display: 'flex', gap: '0.5rem', flexDirection: 'row', alignItems: 'center' }}>
                            <Box sx={{ width: '2.25rem' }}>
                              <Typography>{org.organization_type}</Typography>
                            </Box>
                            <Divider orientation='vertical' flexItem />
                            <ListItemText primary={org.name} />
                          </Box>
                        </MenuItem>

                        {org.patches?.map((patch) => (
                          <MenuItem
                            key={patch.id}
                            selected={selectedOrgId === patch.id}
                            onClick={() => { handleOrgSelect(patch.id, patch.name); setAnchorEl(null) }}
                            sx={{ pl: 6 }}
                          >
                            <ListItemText
                              primary={patch.name}
                              secondary="Patch"
                              slotProps={{ primary: { variant: 'body2' } }}
                            />
                          </MenuItem>
                        ))}
                      </Box>
                    ))
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
